import React, { ChangeEvent, FocusEvent, useRef, useState } from "react";

import { Input } from "antd";
import { RuleObject } from "antd/es/form";
import { NamePath } from "antd/es/form/interface";
import type { TextAreaProps as AntTextAreaProps } from "antd/es/input";

import { parseInputRules } from "utils";

import { Form, InputLabel } from "components";

const AntInput = Input.TextArea;

type TextAreaProps = Pick<
  AntTextAreaProps,
  "allowClear" | "autoSize" | "disabled" | "maxLength" | "placeholder" | "rows" | "defaultValue" | "onBlur" | "onChange"
> & {
  name: NamePath;
  label: string;
  dataTestId?: string;
  className?: string;
  initialValue?: string | null;
  message?: string;
  rules?: RuleObject[];
};

export const TextArea = ({
  dataTestId,
  allowClear,
  autoSize,
  className = "",
  disabled,
  initialValue,
  maxLength,
  name,
  label,
  placeholder,
  message,
  rows,
  rules,
  defaultValue,
  onBlur = () => {},
  onChange = () => {},
}: TextAreaProps) => {
  const inputElement = useRef<HTMLTextAreaElement>(null);
  const [hasBeenFocused, setHasBeenFocused] = useState(false);
  const [internalValue, setInternalValue] = useState(initialValue);

  const handleBlur = (e: FocusEvent<HTMLTextAreaElement>) => {
    setHasBeenFocused(false);
    onBlur?.(e);
  };

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setInternalValue(e.target.value);
    onChange?.(e);
  };

  const handleLabelClick = () => {
    setHasBeenFocused(true);
    inputElement.current?.focus();
  };

  const active = hasBeenFocused || !!internalValue;
  const required = rules && rules[0] && rules[0].required;

  return (
    <Form.Item
      className={`floating-input text-area ${hasBeenFocused ? "active" : ""} ${disabled ? "disabled" : "message"} ${className}`}
      colon={false}
      label={placeholder ? null : <InputLabel active={active} required={required} label={label} handleClick={handleLabelClick} />}
      name={name}
      rules={parseInputRules(rules, message)}
      initialValue={initialValue || undefined}
    >
      <AntInput
        data-testid={dataTestId}
        autoSize={autoSize}
        allowClear={allowClear}
        defaultValue={defaultValue}
        disabled={disabled}
        maxLength={maxLength}
        ref={inputElement}
        placeholder={placeholder}
        rows={rows}
        onChange={handleChange}
        onFocus={() => setHasBeenFocused(true)}
        onBlur={handleBlur}
      />
    </Form.Item>
  );
};
